/**
 * Online Status Detection Hook
 *
 * Tracks browser online/offline status and provides toast notifications
 * when connectivity changes.
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sentry } from "@/lib/sentry";
import { translateToast } from "@/lib/i18n-toast";

export interface OnlineStatus {
  /** Whether the browser is currently online */
  isOnline: boolean;
  /** Whether we've detected an offline event since mount */
  wasOffline: boolean;
  /** Timestamp of last status change */
  lastStatusChange: Date | null;
}

/**
 * Hook to track online/offline status with notifications
 *
 * @param options.showToasts - Whether to show toast notifications (default: true)
 * @param options.trackInSentry - Whether to track status changes in Sentry (default: true)
 */
export function useOnlineStatus(
  options: {
    showToasts?: boolean;
    trackInSentry?: boolean;
  } = {},
): OnlineStatus {
  const { showToasts = true, trackInSentry = true } = options;
  const { t } = useTranslation();

  const [status, setStatus] = useState<OnlineStatus>({
    isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
    wasOffline: false,
    lastStatusChange: null,
  });

  const handleOnline = useCallback(() => {
    setStatus((prev) => ({
      isOnline: true,
      wasOffline: prev.wasOffline,
      lastStatusChange: new Date(),
    }));

    if (showToasts) {
      toast.success(translateToast("toast.online.title"), {
        description: translateToast("toast.online.description"),
        duration: 3000,
      });
    }

    if (trackInSentry) {
      // Connectivity changes are useful context for debugging, but they are not actionable issues.
      // Record as breadcrumbs instead of opening Sentry Issues.
      Sentry.addBreadcrumb({
        category: "connectivity",
        message: "Connection restored",
        level: "info",
        data: {
          wasOffline: true,
          offlineDurationMs: status.lastStatusChange
            ? Date.now() - status.lastStatusChange.getTime()
            : null,
        },
      });
    }
  }, [showToasts, trackInSentry, status.lastStatusChange, t]);

  const handleOffline = useCallback(() => {
    setStatus({
      isOnline: false,
      wasOffline: true,
      lastStatusChange: new Date(),
    });

    if (showToasts) {
      toast.error(translateToast("toast.offline.title"), {
        description: translateToast("toast.offline.description"),
        duration: 5000,
      });
    }

    if (trackInSentry) {
      // Breadcrumb only (avoid creating a noisy Sentry Issue for expected offline events).
      Sentry.addBreadcrumb({
        category: "connectivity",
        message: "Connection lost",
        level: "warning",
        data: { timestamp: new Date().toISOString() },
      });
    }
  }, [showToasts, trackInSentry, t]);

  useEffect(() => {
    // Listen for online/offline events
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Also check periodically in case events are missed
    const checkInterval = setInterval(() => {
      const currentlyOnline = navigator.onLine;
      setStatus((prev) => {
        if (prev.isOnline !== currentlyOnline) {
          if (currentlyOnline) {
            handleOnline();
          } else {
            handleOffline();
          }
        }
        return prev;
      });
    }, 30000); // Check every 30 seconds

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(checkInterval);
    };
  }, [handleOnline, handleOffline]);

  return status;
}

/**
 * Provider component to enable online status tracking app-wide
 * Use this in App.tsx to enable notifications without using the hook elsewhere
 */
export function OnlineStatusProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  useOnlineStatus();
  return <>{children}</>;
}
